import { useCallback, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { MemberAssignedRating } from "../types";
import { isGroupLocked } from "../types";
import { useEnsureGroup } from "../hooks/useEnsureGroup";
import { usePageSetup } from "../hooks/usePageSetup";
import { useShortcuts } from "../hooks/useShortcuts";
import { selectMember, useGroupStore } from "../store/groupStore";
import { confirmAction, notify } from "../store/uiStore";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/Button";
import { TagPill } from "../components/Pills";
import { StatStrip, type StatTileProps } from "../components/StatStrip";
import { ErrorState, SkeletonStatStrip } from "../components/ScreenStates";
import { AssignedRatingsGrid } from "../components/member/AssignedRatingsGrid";
import { RatingChangeModal, type RatingChangeDetails } from "../components/member/RatingChangeModal";
import { LOCKED_TOOLTIP, SUPERSEDED_TOOLTIP } from "../components/group/memberOrdering";
import { PriorYearRatingsCard } from "../components/member/PriorYearRatingsCard";
import { formatCurrency, formatHeight, formatInteger, fullName } from "../utils/format";

type PendingChange = { kind: "override"; rating: MemberAssignedRating; debits: number } | { kind: "remove"; rating: MemberAssignedRating } | { kind: "field"; rating: MemberAssignedRating; patch: { isExcluded?: boolean; irixSource?: boolean; uwDollars?: number | null } };

export function MemberOverviewPage() {
  const { groupId = "", memberId = "" } = useParams();
  const navigate = useNavigate();
  const ensure = useEnsureGroup(groupId);
  const group = useGroupStore((state) => (state.group?.id === groupId ? state.group : null));
  const totals = useGroupStore((state) => (state.group?.id === groupId ? state.totals : null));
  const member = useGroupStore(selectMember(memberId));
  const updateAssignedRating = useGroupStore((state) => state.updateAssignedRating);
  const setAssignedRatingIrixSource = useGroupStore((state) => state.setAssignedRatingIrixSource);
  const isRecalculating = useGroupStore((state) => state.isRecalculating);
  const removeAssignedRating = useGroupStore((state) => state.removeAssignedRating);
  const [pending, setPending] = useState<PendingChange | null>(null);
  const isLocked = group ? isGroupLocked(group) : false;
  const lockedTitle = !group || !isLocked ? undefined : group.supersededByGroupId ? SUPERSEDED_TOOLTIP : LOCKED_TOOLTIP;
  const memberName = member ? fullName(member.firstName, member.lastName) : "Member";
  const breadcrumb = useMemo(() => (group && member ? [{ label: "Recent Groups", to: "/uw/recent" }, { label: group.name, to: `/uw/groups/${group.id}`, status: group.status }, { label: memberName, to: "" }] : null), [group, member, memberName]);
  usePageSetup("Member Overview", breadcrumb);

  const addConditionTo = `/uw/groups/${groupId}/members/${memberId}/conditions/select`;
  const handleAddCondition = useCallback(() => {
    if (!isLocked) navigate(addConditionTo);
  }, [navigate, addConditionTo, isLocked]);
  const handleEditMember = useCallback(() => {
    if (!isLocked) navigate(`/uw/groups/${groupId}/members/${memberId}/edit`);
  }, [navigate, groupId, memberId, isLocked]);
  const shortcuts = useMemo(() => ({ add: handleAddCondition, edit: handleEditMember }), [handleAddCondition, handleEditMember]);
  useShortcuts(shortcuts);

  const isAiV2Member = Boolean(group?.isAiV2Copy && member?.wasOnSkynetOriginal);
  const needsChangeReason = useCallback((rating: MemberAssignedRating) => isAiV2Member && rating.wasOnSkynetOriginal, [isAiV2Member]);

  const handleDebitsChange = useCallback(async (rating: MemberAssignedRating, debits: number) => {
    setPending({ kind: "override", rating, debits });
    return true;
  }, []);

  const handleUwDollarsChange = useCallback(
    async (rating: MemberAssignedRating, uwDollars: number | null) => {
      if (needsChangeReason(rating)) {
        setPending({ kind: "field", rating, patch: { uwDollars } });
        return true;
      }
      return updateAssignedRating(memberId, rating.id, { uwDollars });
    },
    [memberId, updateAssignedRating, needsChangeReason],
  );

  const handleFieldChange = useCallback(
    (rating: MemberAssignedRating, patch: { isExcluded?: boolean; irixSource?: boolean }) => {
      if (needsChangeReason(rating)) setPending({ kind: "field", rating, patch });
      // IRIX Source cannot change a debit, so it takes the flag endpoint: no engine run, no group reload behind the click.
      else if (patch.irixSource !== undefined && patch.isExcluded === undefined) void setAssignedRatingIrixSource(memberId, rating.id, patch.irixSource);
      else void updateAssignedRating(memberId, rating.id, patch);
    },
    [memberId, updateAssignedRating, setAssignedRatingIrixSource, needsChangeReason],
  );

  // A note documents the rating; it does not change it, so it never asks for a change reason.
  const handleNotesChange = useCallback((rating: MemberAssignedRating, notes: string | null) => updateAssignedRating(memberId, rating.id, { notes }), [memberId, updateAssignedRating]);

  const handleRemove = async (rating: MemberAssignedRating) => {
    if (needsChangeReason(rating)) {
      setPending({ kind: "remove", rating });
      return;
    }
    const confirmed = await confirmAction({ title: "Remove rating", message: `Remove ${rating.conditionName}: ${rating.ratingText} from ${memberName}?`, confirmLabel: "Remove", variant: "danger" });
    if (confirmed && (await removeAssignedRating(memberId, rating.id))) notify(`Removed ${rating.ratingText}.`, "success");
  };

  const handlePendingConfirm = async (details: RatingChangeDetails) => {
    if (!pending) return;
    const reason = { changeReason: details.changeReason, changeExplanation: details.changeExplanation };
    let saved = false;
    if (pending.kind === "override") saved = await updateAssignedRating(memberId, pending.rating.id, { assignedDebits: pending.debits, notes: details.notes, ...reason });
    else if (pending.kind === "field") saved = await updateAssignedRating(memberId, pending.rating.id, { ...pending.patch, notes: details.notes ?? pending.rating.notes, ...reason });
    else saved = await removeAssignedRating(memberId, pending.rating.id, details.changeReason, details.changeExplanation);
    if (saved) {
      setPending(null);
      notify(pending.kind === "remove" ? `Removed ${pending.rating.ratingText}.` : `Saved ${pending.rating.ratingText}.`, "success");
    }
  };

  if (ensure.error) return <ErrorState message={ensure.error} onRetry={ensure.retry} />;
  if (!group || !totals || !member) {
    if (!ensure.isLoading) return <ErrorState message="Member not found in this group." onRetry={ensure.retry} />;
    return (
      <div className="space-y-5">
        <PageHeader title="Loading member" />
        <SkeletonStatStrip tiles={4} />
      </div>
    );
  }

  const memberTotals = totals.perMember[member.id];
  const subscriber = member.subscriberMemberId && member.subscriberMemberId !== member.id ? group.members.find((candidate) => candidate.id === member.subscriberMemberId) : null;
  const tiles: StatTileProps[] = [
    { label: "Rx Debits", value: formatInteger(memberTotals?.rxDebits ?? 0) },
    { label: "Condition Debits", value: formatInteger(memberTotals?.conditionDebits ?? 0) },
    { label: "Build", value: formatInteger(memberTotals?.build ?? 0) },
    { label: "Total Debits", value: formatInteger(memberTotals?.totalDebits ?? 0), emphasis: true },
    ...((memberTotals?.uwDollars ?? 0) > 0 ? [{ label: "UW Dollars", value: formatCurrency(memberTotals?.uwDollars ?? 0) }] : []),
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title={memberName}
        badges={
          <>
            {member.memberType === "SpockCohort" && <TagPill label={`Spock ${member.cohortYear} ${member.cohortGender}`} tone="navy" />}
            {member.memberType === "GradientDoe" && <TagPill label="Gradient" tone="green" />}
            {member.isExcluded && <TagPill label="Excluded" tone="warning" />}
          </>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span>{member.relation}</span>
            <span>Age {memberTotals?.age ?? "–"}</span>
            <span>Sex {member.gender ?? "–"}</span>
            <span>Height {formatHeight(member.heightFeet, member.heightInches) || "–"}</span>
            <span>Weight {member.weightLbs ?? "–"}</span>
            <span>ZIP {member.postalCode ?? "–"}</span>
            {subscriber && (
              <span>
                Subscriber{" "}
                <Link to={`/uw/groups/${groupId}/members/${subscriber.id}`} className="text-brand-navy underline-offset-2 hover:underline">
                  {fullName(subscriber.firstName, subscriber.lastName)}
                </Link>
              </span>
            )}
          </span>
        }
        actions={
          <>
            <Button onClick={handleAddCondition} disabled={isLocked} title={lockedTitle}>
              Add Condition
            </Button>
            <Button variant="secondary" onClick={handleEditMember} disabled={isLocked} title={lockedTitle}>
              Edit Demographics
            </Button>
          </>
        }
      />
      <StatStrip tiles={tiles} ariaLabel="Member totals" recalculating={isRecalculating} />
      <PriorYearRatingsCard groupId={groupId} memberId={member.id} />
      <AssignedRatingsGrid
        ratings={member.assignedRatings}
        isLocked={isLocked}
        showChangeReason={group.isAiV2Copy}
        addConditionTo={addConditionTo}
        onDebitsChange={handleDebitsChange}
        onUwDollarsChange={handleUwDollarsChange}
        onToggleExcluded={(rating, isExcluded) => handleFieldChange(rating, { isExcluded })}
        onIrixSourceChange={(rating, irixSource) => handleFieldChange(rating, { irixSource })}
        onNotesChange={handleNotesChange}
        onRemove={(rating) => void handleRemove(rating)}
      />
      {pending && (
        <RatingChangeModal
          title={pending.kind === "remove" ? "Remove rating" : pending.kind === "override" ? "Override debits" : "Record change"}
          description={pending.kind === "override" ? `Set ${pending.rating.ratingText} to ${pending.debits} debits (default ${pending.rating.defaultDebits}).` : `${pending.rating.conditionName}: ${pending.rating.ratingText}`}
          requireNote={pending.kind === "override"}
          requireChangeReason={needsChangeReason(pending.rating)}
          initialNotes={pending.rating.notes}
          confirmLabel={pending.kind === "remove" ? "Remove" : "Save"}
          confirmVariant={pending.kind === "remove" ? "danger" : "primary"}
          onConfirm={handlePendingConfirm}
          onCancel={() => setPending(null)}
        />
      )}
    </div>
  );
}
